import type { CacheState, IntelState, Manuscript, Project } from "../lib/types.js";
import { Icon, type IconName } from "../components/Icon.js";
import { ManuscriptTree } from "./ManuscriptTree.js";
import { formatNumber } from "../lib/format.js";

export type SidebarSection =
  | "overview"
  | "manuscript"
  | "storyBible"
  | "characters"
  | "relationships"
  | "locations"
  | "timeline"
  | "plotThreads"
  | "notes"
  | "research"
  | "insights"
  | "cache"
  | "audiobook"
  | "publish"
  | "vault";

const SECTIONS: { key: SidebarSection; label: string; icon: IconName }[] = [
  { key: "overview", label: "Overview", icon: "home" },
  { key: "manuscript", label: "Manuscript", icon: "layers" },
  { key: "storyBible", label: "Story Bible", icon: "book" },
  { key: "characters", label: "Characters", icon: "users" },
  { key: "relationships", label: "Relationships", icon: "heart" },
  { key: "locations", label: "Locations", icon: "map" },
  { key: "timeline", label: "Timeline", icon: "timeline" },
  { key: "plotThreads", label: "Plot Threads", icon: "thread" },
  { key: "notes", label: "Notes", icon: "note" },
  { key: "research", label: "Research", icon: "search" },
  { key: "insights", label: "Insights", icon: "sparkles" },
  { key: "cache", label: "Book Cache", icon: "history" },
  { key: "audiobook", label: "Audiobook", icon: "volume" },
  { key: "publish", label: "Publish", icon: "download" },
  { key: "vault", label: "Vault", icon: "vault" }
];

interface Props {
  project: Project;
  manuscript: Manuscript;
  section: SidebarSection;
  onSectionChange: (section: SidebarSection) => void;
  activeSceneId: string | null;
  projectId: string;
  onSelectScene: (id: string) => void;
  onManuscriptChanged: () => Promise<void>;
  onRequestDelete: (kind: "chapter" | "scene" | "part", id: string, name: string) => void;
  onGoDashboard: () => void;
  /** Whether the app-nav rail is collapsed to an icon strip. */
  navCollapsed: boolean;
  onToggleNav: () => void;
  cacheState: CacheState | null;
  cacheBuilding: boolean;
  insightsState: IntelState | null;
  insightsSyncing: boolean;
  charsOrganizing: boolean;
  audiobookRendering: boolean;
}

/** Freshness dot shown on the Book Cache nav item. */
function cacheDotTitle(state: CacheState | null): string | null {
  if (state === "fresh") return "Book cache is up to date";
  if (state === "stale") return "Book cache is out of date — new content to cache";
  if (state === "empty") return "Nothing written to cache yet";
  return null;
}

/** Freshness dot shown on the Insights nav item. */
function insightsDotTitle(state: IntelState | null): string | null {
  if (state === "fresh") return "Insights are up to date with your manuscript";
  if (state === "stale") return "Insights are out of date — run Auto-populate to refresh";
  return null;
}

export function Sidebar({
  project,
  manuscript,
  section,
  onSectionChange,
  activeSceneId,
  projectId,
  onSelectScene,
  onManuscriptChanged,
  onRequestDelete,
  onGoDashboard,
  navCollapsed,
  onToggleNav,
  cacheState,
  cacheBuilding,
  insightsState,
  insightsSyncing,
  charsOrganizing,
  audiobookRendering
}: Props) {
  return (
    <div className={`sidebar${navCollapsed ? " nav-collapsed" : ""}`}>
      <div className="nav-rail">
        <div className="nav-rail-head">
          <button className="btn btn-icon btn-ghost btn-sm" title="Dashboard" onClick={onGoDashboard}>
            <Icon name="home" size={16} />
          </button>
          {!navCollapsed && <span className="grow" />}
          <button
            className="btn btn-icon btn-ghost btn-sm"
            title={navCollapsed ? "Expand navigation" : "Collapse navigation"}
            onClick={onToggleNav}
          >
            <Icon name="panelLeft" size={16} />
          </button>
        </div>

        <nav className="sidebar-nav">
          {SECTIONS.map((s) => (
            <button
              key={s.key}
              className={`nav-item ${s.key === section ? "active" : ""}`}
              title={s.label}
              onClick={() => onSectionChange(s.key)}
            >
              <Icon name={s.icon} size={16} className="nav-ico" />
              <span className="nav-label truncate">{s.label}</span>
              {s.key === "cache" && cacheBuilding && (
                <span className="cache-spin" title="Refreshing the book cache…">
                  <Icon name="history" size={13} className="spin" />
                </span>
              )}
              {s.key === "cache" && !cacheBuilding && cacheState && cacheState !== "empty" && (
                <span className={`cache-dot cache-dot--${cacheState}`} title={cacheDotTitle(cacheState) ?? undefined} />
              )}
              {s.key === "insights" && insightsSyncing && (
                <span className="cache-spin" title="Auto-populate is running…">
                  <Icon name="history" size={13} className="spin" />
                </span>
              )}
              {s.key === "characters" && charsOrganizing && (
                <span className="cache-spin" title="AI is organizing characters…">
                  <Icon name="sparkles" size={13} className="spin" />
                </span>
              )}
              {s.key === "audiobook" && audiobookRendering && (
                <span className="cache-spin" title="Rendering audiobook in the background…">
                  <Icon name="volume" size={13} className="spin" />
                </span>
              )}
              {s.key === "insights" && !insightsSyncing && insightsState && insightsState !== "empty" && (
                <span className={`cache-dot cache-dot--${insightsState}`} title={insightsDotTitle(insightsState) ?? undefined} />
              )}
            </button>
          ))}
        </nav>
      </div>

      {section === "manuscript" && (
        <div className="nav-tree">
          <div className="sidebar-head">
            <div className="grow col" style={{ minWidth: 0, lineHeight: 1.2 }}>
              <span className="truncate strong" style={{ fontSize: 13 }}>{project.title}</span>
              <span className="faint" style={{ fontSize: 11 }}>{formatNumber(manuscript.totals.wordCount)} words</span>
            </div>
          </div>
          <div className="sidebar-content">
            <ManuscriptTree
              projectId={projectId}
              manuscript={manuscript}
              activeSceneId={activeSceneId}
              onSelectScene={onSelectScene}
              onChanged={onManuscriptChanged}
              onRequestDelete={onRequestDelete}
            />
          </div>
        </div>
      )}
    </div>
  );
}
